import Template from "../../components/Template";
import { useNavigate } from 'react-router-dom';
import { useNavigation } from '../../context/NavigationContext';
import React, { useState, useEffect, useRef } from "react";
import api from "../../services/api";
import config from "../../config";
import { formatDateWithHour, formatDateWithoutHour } from "../../utils/date";
import * as XLSX from "xlsx";
import AlertDialog from "../../components/AlertDialog";
import { Link } from "react-router-dom";
import { formatCurrencyFromDb } from "../../utils/masks";
import usePermission from "../../hooks/usePermission";
import useListFilterState from "../../hooks/useListFilterState";

const Atas = () => {
    const navigate = useNavigate();
    const [fornecedores, setFornecedores] = useState([]);
    const { updateMenu } = useNavigation();
    const [atas, setAtas] = useState([]);
    const [filteredAtas, setFilteredAtas] = useState([]);
    const [selectedDoc, setSelectedDoc] = useState(null); // Estado para o documento selecionado
    const [showDialog, setShowDialog] = useState(false); // Estado para controlar o modal    
    const [selectedPdf, setSelectedPdf] = useState("");
    const [currentPage, setCurrentPage] = useState(1); // Página atual
    const [totalRecords, setTotalRecords] = useState(0); // Total de registros    
    const menuRef = useRef(null);
    const menuFiltrarRef = useRef(null);
    const buttonRef = useRef(null);
    const buttonFiltrarRef = useRef(null);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    const { filters, setFilters, searchTerm, setSearchTerm, isSearchActive, setIsSearchActive, pageSize, setPageSize, hasRestoredFilters, clearPersistedState } = useListFilterState('atas', {
        fornecedor: "",
        numeroAta: "",
        numeroPregao: "",
        nomeContratante: "",
        dataInicio: "",
        dataTermino: "",
    });
    const [message, setMessage] = useState("");

    useEffect(() => {
        // Carregar categorias e status da API
        const fetchData = async () => {
            try {
                const [fornecedoresRes] = await Promise.all([
                    api.get("/fornecedores/todos"),

                ]);
                setFornecedores(fornecedoresRes.data);
            } catch (error) {
                console.error("Erro ao carregar dados:", error);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (hasRestoredFilters()) {
            applyFilters();
            return;
        }
        fetchAtas(currentPage, pageSize);
    }, [currentPage]);

    useEffect(() => {
        // Sempre que o termo de pesquisa mudar, atualize os documentos filtrados
        if (searchTerm.trim() === "") {
            setFilteredAtas(atas); // Se não houver termo de pesquisa, mostre todos os documentos
        } else {
            setFilteredAtas(
                atas.filter((doc) =>
                    doc.objeto.toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
        }
    }, [searchTerm, atas]);

    const fetchAtas = async (page, size) => {
        try {
            const response = await api.get(`/atas?page=${page}&pageSize=${size}`);
            setAtas(response.data.records);
            setTotalRecords(response.data.total);
            setPageSize(size); // Atualiza o tamanho
        } catch (error) {
            console.error("Erro ao carregar dados:", error);
        }
    };

    const fetchFilteredAtas = async (query) => {
        try {
            const params = new URLSearchParams(query).toString();
            const response = await api.get(`/atas?${params}`);

            setAtas(response.data.records);
            setTotalRecords(response.data.total);
            setCurrentPage(1);
        } catch (error) {
            console.error("Erro ao aplicar filtros:", error);
        }
    };

    const handleNavigateCadastrar = async (e) => {
        e.preventDefault();
        try {
            navigate('/atas/cadastrar');
        } catch (err) {
            console.error('Documentos Error:', err);
            alert(err.response?.data?.msg || 'Erro ao ir para o cadastro');
        }
    };

    const handleCloseDialog = () => {
        setSelectedDoc(null);
        setShowDialog(false);
    };

    const handleShowPdf = (url) => {
        setSelectedPdf(url);
        setShowDialog(true);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const totalPages = Math.ceil(totalRecords / pageSize);
    const startRecord = (currentPage - 1) * pageSize + 1;
    const endRecord = Math.min(currentPage * pageSize, totalRecords);

    // Janela de páginas exibidas
    const pageWindowSize = 5;
    const startPage = Math.max(1, currentPage - Math.floor(pageWindowSize / 2));
    const endPage = Math.min(totalPages, startPage + pageWindowSize - 1);

    const pageNumbers = Array.from(
        { length: endPage - startPage + 1 },
        (_, i) => startPage + i
    );

    const [columns, setColumns] = useState([
        {
            id: "id",
            label: "ID",
            visible: true,
            formatter: (value) => (
                <Link to={`/atas/editar/${value}`} className="text-stone-600 hover:underline">
                    {value}
                </Link>
            ),
        },
        {
            id: "numeroAta",
            label: "Nº Ata",
            visible: true,
            formatter: (value) => value

        },
        {
            id: "numeroProcesso",
            label: "Nº Processo",
            visible: true,
            formatter: (value) => value,
        },
        {
            id: "modalidade",
            label: "Modalidade",
            visible: true,
            formatter: (value) => value || "",
        },
        {
            id: "numeroPregao",
            label: "Nº Pregão",
            visible: true,
            formatter: (value) => value || "",
        },
        {
            id: "tipo",
            label: "Tipo",
            visible: true,
            formatter: (value) => value || "",
        },
        {
            id: "valorAta",
            label: "Valor da ATA (R$)",
            visible: true,
            formatter: (value) => formatCurrencyFromDb(value),
        },
        {
            id: "percentualDesconto",
            label: "Desconto (%)",
            visible: true,
            formatter: (value) => value || "",
        },
        {
            id: "nomeContratante",
            label: "Contratante",
            visible: true,
            formatter: (value) => value || "",
        },

        {
            id: "fornecedor",
            label: "Fornecedor",
            visible: true,
            formatter: (value) => value?.nomeFornecedor || "",
        },

        {
            id: "dataInicio",
            label: "Data Início",
            visible: true,
            formatter: (value) =>
                value ? formatDateWithoutHour(value) : "", // Formata data com hora
        },

        {
            id: "dataTermino",
            label: "Data Fim",
            visible: true,
            formatter: (value) =>
                value ? formatDateWithoutHour(value) : "", // Formata data com hora
        },

    ]);
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuFiltrarOpen, setMenuFiltrarOpen] = useState(false);

    const toggleColumnVisibility = (id) => {
        setColumns((prevColumns) =>
            prevColumns.map((col) =>
                col.id === id ? { ...col, visible: !col.visible } : col
            )
        );
    };

    // Função para fechar o menu ao clicar fora
    const handleClickOutside = (event) => {
        if (
            buttonRef.current &&
            !buttonRef.current.contains(event.target) &&
            buttonFiltrarRef.current &&
            !buttonFiltrarRef.current.contains(event.target)
        ) {
            setMenuOpen(null);
        }
    };

    useEffect(() => {
        if (menuOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);

        };
    }, [menuOpen]);

    const handleMenuClick = (event) => {
        // Impede a propagação do evento para o handleClickOutside
        event.stopPropagation();
    };

    const applyFilters = () => {
        setCurrentPage(1);
        const query = {
            ...filters,
            page: 1,
            pageSize,
        };

        fetchFilteredAtas(query);
    };

    const clearFilters = () => {
        clearPersistedState();
        setSearchTerm(""); // Limpa o termo de pesquisa
        setCurrentPage(1); // Reinicia a páginação

        setFilters({
            fornecedor: "",
            numeroAta: "",
            numeroPregao: "",
            nomeContratante: "",
            dataInicio: "",
            dataTermino: "",
        });
        fetchAtas(1, pageSize); // Restaura os dados padrão
    };

    const handleSearch = () => {
        setCurrentPage(1); // Reinicia para a primeira página
        const query = {
            ...filters,
            page: 1,
            pageSize,
        };
        fetchFilteredAtas(query); // Executa a busca com o termo atual
    };

    // Função para exportar a tabela
    const exportToExcel = () => {
        if (atas.length === 0) {
            setMessage('Não há dados para exportar!')
            setShowSuccessDialog(true);
            return;
        }

        // Define quais colunas são monetárias
        const monetaryColumns = ['valorAta']; // substitua pelos IDs reais

        // Mapeia os dados da tabela para um formato plano
        const dataToExport = atas.map((doc) => {
            const row = {};
            columns.forEach((col) => {
                if (col.visible) {
                    let value = col.formatter ? col.formatter(doc[col.id]) : doc[col.id];

                    // Remove formatação monetária (p.ex. 'R$', '.', ',') e converte para número
                    if (monetaryColumns.includes(col.id)) {
                        value = Number(String(value)
                            .replace(/[R$\s.]/g, '')
                            .replace(',', '.')
                        );
                    }

                    row[col.label] = value;
                }
            });
            return row;
        });

        // Cria uma nova planilha do Excel
        const worksheet = XLSX.utils.json_to_sheet(dataToExport);

        // Formata células monetárias explicitamente como números no Excel
        const columnLabels = columns.filter(col => col.visible).map(col => col.label);
        monetaryColumns.forEach((colId) => {
            const colIndex = columnLabels.indexOf(columns.find(c => c.id === colId)?.label);
            if (colIndex >= 0) {
                const columnLetter = XLSX.utils.encode_col(colIndex);
                const range = XLSX.utils.decode_range(worksheet['!ref']);

                for (let i = range.s.r + 1; i <= range.e.r; i++) {
                    const cellAddress = `${columnLetter}${i + 1}`;
                    if (worksheet[cellAddress]) {
                        worksheet[cellAddress].t = 'n';
                        worksheet[cellAddress].z = '#,##0.00';
                    }
                }
            }
        });

        // Cria um novo workbook e adiciona a worksheet
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Atas");

        // Exporta o arquivo
        XLSX.writeFile(workbook, "atas.xlsx");
    };

    return (
        <Template>
            <div className="h-auto w-full p-4 md:p-8 flex flex-col gap-6">
                <div className="flex flex-wrap justify-between items-center gap-4">
                    <div className="text-stone-800 text-xl md:text-2xl font-medium font-['Inter']">ATAS</div>
                </div>
                <div className="flex flex-wrap gap-2 md:gap-4 items-center">
                    <div className="flex-grow min-w-0 flex items-center gap-2">
                        <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-tight">Ver</div>
                        <select
                            className="w-20 h-8 pl-2 pr-2 py-1 bg-white rounded-md border border-slate-300 text-sm text-slate-700"
                            value={pageSize} // O valor do tamanho da página atual
                            onChange={(e) => {
                                const newPageSize = parseInt(e.target.value, 10); // Converte para número
                                setPageSize(newPageSize); // Atualiza o tamanho da página no estado
                                setCurrentPage(1); // Reinicia para a primeira página
                                fetchAtas(1, newPageSize); // Atualiza os dados da API
                            }}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-tight">Resultados</div>
                    </div>
                    <div className="flex flex-wrap gap-2 flex-none w-full md:w-auto">
                        <div ref={buttonRef} className="relative justify-end items-center gap-1 flex cursor-pointer" onClick={() => setMenuOpen(menuOpen === "colunas" ? null : "colunas")}>
                            <div className="justify-start items-center gap-2 flex" >
                                <img className="w-6 h-6 relative " src="images/icon/columns.png" />
                                <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal">Colunas</div>
                            </div>
                            <img className="w-6 h-4 px-1 justify-center items-center flex" src="images/icon/chevron-down.png" />
                            {menuOpen === "colunas" && (
                                <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-50"
                                    style={{
                                        zIndex: 9999, // Garante que está acima de outros elementos
                                    }}
                                    onClick={handleMenuClick} // Impede que o clique feche o menu
                                >
                                    <div className="p-4 flex flex-col gap-3">
                                        {columns.map((col) => (
                                            <div
                                                key={col.id}
                                                className="flex items-center gap-2"
                                            >
                                                <input
                                                    type="checkbox"
                                                    id={col.id}
                                                    checked={col.visible}
                                                    onChange={() =>
                                                        toggleColumnVisibility(col.id)
                                                    }
                                                />
                                                <label
                                                    htmlFor={col.id}
                                                    className="text-sm text-gray-700"
                                                >
                                                    {col.label}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div ref={buttonFiltrarRef} className="relative justify-start items-center gap-1 flex cursor-pointer" onClick={() => setMenuOpen(menuOpen === "filtro" ? null : "filtro")} >
                            <div className="justify-start items-center gap-2 flex">
                                <img className="w-8 h-8 px-0.5 py-[3px] justify-center items-center flex" src="images/icon/filter.png" />
                                <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal">Filtrar</div>
                            </div>
                            <img className="w-6 h-4 px-1 justify-center items-center flex" src="images/icon/chevron-down.png" />
                            {/* Menu Suspenso */}
                            {menuOpen === "filtro" && (
                                <div
                                    className="absolute top-full left-0 mt-2 w-[350px] bg-white rounded-md shadow-lg z-50 p-4 border border-gray-300"
                                    style={{ zIndex: 9999 }}
                                    onClick={handleMenuClick} // Impede que o clique feche o menu
                                >
                                    <div className="flex flex-col gap-4">
                                        <div className="self-stretch justify-start items-center gap-2.5 inline-flex">
                                            <div className="grow shrink basis-0 text-stone-800 text-xl font-medium">
                                                Filtrar
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Fornecedor
                                            </div>
                                            <select
                                                name="orgaoemissor"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-slate-700 text-base"
                                                value={filters.fornecedor}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, fornecedor: e.target.value })
                                                }
                                            >
                                                <option value="">Selecionar</option>
                                                {fornecedores.map((obj) => (
                                                    <option key={obj.id} value={obj.id}>
                                                        {obj.nomeFornecedor.toUpperCase()}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Nº ATA
                                            </div>
                                            <input
                                                type="text"
                                                name="numeroAta"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                placeholder="Digite o número da ATA"
                                                value={filters.numeroAta}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, numeroAta: e.target.value })
                                                }
                                            />
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Nº Pregão
                                            </div>
                                            <input
                                                type="text"
                                                name="numeroPregao"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                placeholder="Digite o número do Pregão"
                                                value={filters.numeroPregao}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, numeroPregao: e.target.value })
                                                }
                                            />
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Contratante
                                            </div>
                                            <input
                                                type="text"
                                                name="nomeContratante"
                                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                placeholder="Digite o nome do contratante"
                                                value={filters.nomeContratante}
                                                onChange={(e) =>
                                                    setFilters({ ...filters, nomeContratante: e.target.value })
                                                }
                                            />
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <div className="text-slate-700 text-sm font-medium leading-tight">
                                                Data de Vigência
                                            </div>
                                            <div className="flex gap-2">
                                                <input
                                                    type="date"
                                                    name="dataInicio"
                                                    className="w-1/2 px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                    value={filters.dataInicio}
                                                    onChange={(e) =>
                                                        setFilters({ ...filters, dataInicio: e.target.value })
                                                    }
                                                />
                                                <input
                                                    type="date"
                                                    name="dataTermino"
                                                    className="w-1/2 px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                                                    value={filters.dataTermino}
                                                    onChange={(e) =>
                                                        setFilters({ ...filters, dataTermino: e.target.value })
                                                    }
                                                />
                                            </div>
                                        </div>

                                        <div className="self-stretch flex items-center justify-between gap-4 mt-4">
                                            <button
                                                className="px-4 py-2 w-full bg-white rounded-md border border-stone-500 justify-center items-center gap-2 flex"
                                                onClick={clearFilters}
                                            >
                                                <img className="w-4 h-4 relative" src="images/icon/eraser.png" />
                                                <div className="text-stone-500 text-sm font-medium font-['Inter'] leading-normal" >Limpar</div>
                                            </button>
                                            <button
                                                className="px-4 py-2 w-full bg-stone-600 rounded-md justify-center items-center gap-2 flex"
                                                onClick={applyFilters}
                                            >
                                                <img className="w-4 h-4 relative" src="images/icon/filter-white.png" />
                                                <div className="text-white text-sm font-medium font-['Inter'] leading-normal" >Filtrar</div>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                        </div>
                        <div className="justify-start items-center gap-2 flex">
                            {!isSearchActive ? (
                                // Conteúdo original da div "Pesquisar"
                                <div
                                    className="justify-start items-center gap-2 flex cursor-pointer"
                                    onClick={() => setIsSearchActive(true)} // Ativar o estado de pesquisa
                                >
                                    <img className="w-10 h-10 relative" src="images/icon/search.png" alt="Ícone Pesquisar" />
                                    <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal">Pesquisar</div>
                                </div>
                            ) : (
                                // Conteúdo alternativo ao ativar o estado de pesquisa
                                <div className="h-10 justify-start items-center gap-2 inline-flex">
                                    <div className="h-9 px-3 py-2 bg-white rounded-md border border-slate-300 justify-start items-center flex">
                                        <input
                                            type="text"
                                            placeholder="Pesquisar"
                                            className="w-full text-slate-400 text-base font-normal font-['Inter'] leading-tight focus:outline-none"
                                            value={filters.objeto}
                                            onChange={(e) =>
                                                setFilters({ ...filters, objeto: e.target.value })
                                            }
                                        />
                                        <img className="w-6 h-6 p-[3.33px] justify-center items-center flex cursor-pointer" src="images/icon/close.png" onClick={() => {
                                            setIsSearchActive(false);
                                        }} />
                                    </div>
                                    <button
                                        className="px-4 py-2 bg-stone-600 rounded-md justify-center items-center gap-2 flex"
                                        onClick={handleSearch}
                                    >
                                        <img className="w-4 h-4 relative" src="images/icon/search-white.png" alt="Ícone Pesquisar" />
                                        <div className="text-white text-sm font-medium font-['Inter'] leading-normal">Pesquisar</div>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="self-stretch flex justify-between gap-4">
                        {usePermission('EXPORTAR_ATA') && (
                            <button type='button' className="flex px-4 py-2 bg-white/40 rounded-md border border-stone-600 justify-center items-center gap-2 cursor-pointer" onClick={exportToExcel}>
                                <img className="w-4 h-4" src="images/icon/upload.png" />
                                <div className="text-stone-600 text-sm font-medium font-['Inter'] leading-normal">Exportar</div>
                            </button>
                        )
                        }
                        {usePermission('CRIAR_ATA') && (
                            <button type='button' className="px-4 py-2 bg-stone-600 text-white rounded-md flex items-center gap-2" onClick={handleNavigateCadastrar}>
                                <img className="w-4 h-4" src="images/icon/plus-circle.png" />
                                <div className="text-white text-sm font-medium font-['Inter'] leading-normal" >Cadastrar</div>
                            </button>
                        )}
                    </div>
                </div>

                <div className="overflow-x-auto md:overflow-x-visible">
                    <table className="min-w-full bg-white border-collapse border border-gray-200">
                        <thead className="bg-stone-200">
                            <tr>
                                {columns
                                    .filter((col) => col.visible)
                                    .map((col) => (
                                        <th className="px-4 py-2 border border-slate-200 text-slate-800 text-base font-bold">{col.label}</th>
                                    ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredAtas.map((doc) => (
                                <tr key={doc.id}>
                                    {columns
                                        .filter((col) => col.visible)
                                        .map((col) => (
                                            <td key={`${doc.id}-${col.id}`} className="px-4 py-2 border border-slate-200 text-slate-700 text-base">{col.formatter(doc[col.id])}</td>
                                        ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="self-stretch justify-end items-center gap-2.5 inline-flex">
                    <div className="grow shrink basis-0 text-slate-500 text-base font-normal font-['Inter'] leading-[29.60px]">
                        Mostrando de {startRecord} até {endRecord} de {totalRecords}{" "} registros.
                    </div>
                    <div className="justify-start items-start gap-4 flex">
                        <button
                            className={`w-10 h-10 bg-white/0 rounded border border-[#bebebe] justify-center items-center flex ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-300 cursor-pointer"
                                }`}
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(currentPage - 1)}
                        >
                            <img className="w-6 h-6 relative" src="images/icon/keyboard-arrow-left.png" />
                        </button>

                        {pageNumbers.map((page) => (
                            <button
                                key={page}
                                className={`w-10 h-10 rounded  justify-center items-center flex text-base font-semibold font-['Inter'] ${page === currentPage
                                    ? "bg-stone-600 text-white"
                                    : "bg-gray-200 text-gray-600"
                                    }`}
                                onClick={() => handlePageChange(page)}
                            >
                                {page}
                            </button>
                        ))}
                        <button className={`w-10 h-10 bg-white/0 rounded border border-[#bebebe] justify-center items-center flex 
                        ${currentPage === totalPages ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                            disabled={currentPage === totalPages}
                            onClick={() => handlePageChange(currentPage + 1)}
                        >
                            <img className="w-6 h-6 relative" src="images/icon/keyboard-arrow-right.png" />
                        </button>
                    </div>
                </div>
                {showDialog && (
                    <div className="fixed top-0 left-0 w-full h-full bg-black/90 z-50 p-10 flex-col justify-start items-end gap-10 inline-flex overflow-hidden">
                        <div className=" rounded-lg w-full max-w-[80%] h-full max-h-[90%] flex flex-col">
                            <div className="flex justify-between items-center p-4">
                                <div className="flex items-center gap-4">
                                    <a href={config.urlArquivo(selectedPdf)} download target="_blank">
                                        <img className="w-6 h-6 relative  overflow-hidden" src="images/icon/download.png" />
                                    </a>
                                    <div className="w-6 h-6 relative  overflow-hidden" />
                                    <div className="w-6 h-6 relative  overflow-hidden" />
                                </div>
                                <img className="w-6 h-6 relative  overflow-hidden cursor-pointer" src="images/icon/x.png" onClick={handleCloseDialog} />
                            </div>
                            <div className="flex-grow flex justify-center items-center p-4 overflow-hidden">
                                <iframe
                                    src={`${config.urlArquivo(selectedPdf)}#toolbar=0`}
                                    className="w-full h-full border-0"
                                    style={{ border: 'none' }}
                                    title="PDF Viewer"
                                />
                            </div>
                        </div>
                    </div>
                )}

                <AlertDialog
                    isVisible={showSuccessDialog}
                    message={message}
                    onClose={() => setShowSuccessDialog(false)}
                />
            </div>
        </Template>
    )
}
export default Atas;